import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { CompanyStorageOperations } from '../../models/company.storage.operations.model';
import { ProviderOrder } from '../../models/provider.order.model';
import { PosSale } from '../../models/pos.sale.model';
import { ProviderInvoice } from '../../models/provider.invoice.model';
import { Invoice } from '../../models/invoice.model';
import { Order } from '../../models/order.model';
import { Operation } from '../../models/operation.model';
import { authToken } from '../../scripts/authentication.script';

export class StorageOperationDA {
  static apiResponse: ApiResponse

  public static async getStorageOperations(
    id_company: string,
    id_storage_center: string,
    sort_field: string,
    sort_mode: string,
    page: number = 1,
    limit: number = 20,
    type: string = '',
    date_from: string = '',
    date_to: string = '',
    search: string = ''
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyStorageOperations: CompanyStorageOperations | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/storage.operations.data.json').then(response => { companyStorageOperations = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyStorageOperations!.id_company === id_company && companyStorageOperations!.id_storage_center === id_storage_center) {
        companyStorageOperations!.filter = { sort_field: sort_field, sort_mode: sort_mode }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyStorageOperations
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const params = new URLSearchParams()
      if (sort_field) params.append('sort_by', sort_field)
      if (sort_mode) params.append('sort_order', sort_mode)
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (type) params.append('type', type)
      if (date_from) params.append('date_from', date_from)
      if (date_to) params.append('date_to', date_to)
      if (search) params.append('search', search)

      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/storage-operations/${id_storage_center}?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async getStorageOperationsByIds(
    id_company: string,
    ids: string[]
  ): Promise<Operation[]> {
    if (!ids || ids.length === 0) return []

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      return []
    } else {
      const operations: Operation[] = []

      await Promise.all(ids.map(async (id) => {
        try {
          let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/storage-operations/operation/${id}`
          let config: any = { headers: { Authorization: authToken() } }
          const response = await axios.get(url, config)
          const data = response.data?.data ?? response.data
          if (data) operations.push(data)
        } catch (_error) {
          // Operation not found or deleted — skip
        }
      }))

      return operations
    }
  }

  public static async getStockAvailability(
    id_company: string,
    variant_ids: string[],
    id_storage_center?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: {}
      }
    } else {
      const params = new URLSearchParams()
      for (const id of variant_ids) {
        params.append('variant_ids[]', id)
      }
      if (id_storage_center) {
        params.append('id_storage_center', id_storage_center)
      }
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/stock/availability?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async postStorageOperations(
    id_company: string,
    type: string,
    source: { id: string, type: string, number: string } | null,
    id_source_location: string | null,
    id_destination_location: string | null,
    lines: { id_variant: string, quantity: number }[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: { id: 'dev-operation-id' }
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/storage-operations`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        type: type,
        lines: lines
      }

      if (source) data.source = source
      if (id_source_location) data.id_source_location = id_source_location
      if (id_destination_location) data.id_destination_location = id_destination_location

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }
}
